import { TooltipPlacement } from 'antd/lib/tooltip';
import i18n from 'i18next';
import { isRtl } from 'utils/constants/app/rtl';
import SpringConfigs from 'utils/constants/swarm/spring-configs';
import { TOUR_STEPS_TYPES } from 'utils/constants/tour';

export const TOUR_ACTIVE_CLASS = 'tourWrapper__active';

export type TTourResourceKey = 'sportsbook' | 'casino' | 'esport' | null;

export const SHOW_TOUR_PAGES: { [key: string]: TTourResourceKey } = {
  CASINO: 'casino',
  SPORTSBOOK: 'sportsbook',
  ESPORT: 'esport'
};

export const TOUR_PATH_NAMESPACE = (): TTourResourceKey => {
  const pageAlias = decodeURI(window.location.href).replace(/\/+$/, '');

  if (
    SpringConfigs.PAGE_URLS.casino &&
    (pageAlias.includes(SpringConfigs.PAGE_URLS.casino.replace(/\/+$/, '')) ||
      SpringConfigs.PAGE_URLS.homepageKey === SHOW_TOUR_PAGES.CASINO)
  ) {
    return SHOW_TOUR_PAGES.CASINO;
  }

  if (
    SpringConfigs.PAGE_URLS.prematch &&
    (pageAlias.includes(SpringConfigs.PAGE_URLS.prematch.replace(/\/+$/, '')) ||
      SpringConfigs.PAGE_URLS.homepageKey === SHOW_TOUR_PAGES.SPORTSBOOK)
  ) {
    return SHOW_TOUR_PAGES.SPORTSBOOK;
  }

  if (
    SpringConfigs.PAGE_URLS.esport &&
    (pageAlias.includes(SpringConfigs.PAGE_URLS.esport.replace(/\/+$/, '')) ||
      SpringConfigs.PAGE_URLS.homepageKey === SHOW_TOUR_PAGES.ESPORT)
  ) {
    return SHOW_TOUR_PAGES.SPORTSBOOK;
  }

  return null;
};

const placement = isRtl ? 'leftTop' : 'rightTop';

type TTourSliderReturnProps = {
  active: boolean;
  step: number;
  selector: TOUR_STEPS_TYPES;
  placement: TooltipPlacement;
  modal: {
    title: string;
    description: string;
  };
};

type TTourItemGenerateStructureProps = {
  index: number;
  currentActiveTourStep: number;
  selector: TOUR_STEPS_TYPES;
  placement: TooltipPlacement;
};

export const TOUR_STEPS_LIST: Record<
  TOUR_STEPS_TYPES,
  ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps) => TTourSliderReturnProps
> = {
  [TOUR_STEPS_TYPES.yourPreferences]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.yourPreferencesSection');
      },
      get description() {
        return i18n.t('generic.yourPreferencesSectionDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.addSports]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addSport');
      },
      get description() {
        return i18n.t('generic.addSportDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.addCompetitions]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addCompetitions');
      },
      get description() {
        return i18n.t('generic.addCompetitionsDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.addCompetitionsTopMatches]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addCompetitions');
      },
      get description() {
        return i18n.t('generic.addCompetitionsDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.addGameCards]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addGameCards');
      },
      get description() {
        return i18n.t('generic.addGameCardsDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.addMarketCards]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addMarketCards');
      },
      get description() {
        return i18n.t('generic.addMarketCardsDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.myCasino]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.yourFavoriteGames');
      },
      get description() {
        return i18n.t('generic.yourFavoriteGamesDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.myCasinoProviderItem]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addFavoriteGameProvider');
      },
      get description() {
        return i18n.t('generic.addFavoriteGameProviderDescription');
      }
    }
  }),
  [TOUR_STEPS_TYPES.myCasinoGameCard]: ({
    index,
    currentActiveTourStep,
    selector,
    placement
  }: TTourItemGenerateStructureProps): TTourSliderReturnProps => ({
    placement,
    active: currentActiveTourStep === index,
    step: index,
    selector,
    modal: {
      get title() {
        return i18n.t('generic.addFavoriteCasinoGame');
      },
      get description() {
        return i18n.t('generic.addFavoriteCasinoGameDescription');
      }
    }
  })
};

export const detectTourMethod = (
  selector: TOUR_STEPS_TYPES,
  index: number,
  currentActiveTourStep: number
) => {
  return TOUR_STEPS_LIST[selector]({
    index,
    currentActiveTourStep,
    selector,
    placement: [
      TOUR_STEPS_TYPES.addGameCards,
      TOUR_STEPS_TYPES.addMarketCards
    ].includes(selector)
      ? 'bottom'
      : placement
  });
};
